import { useCallback, useEffect, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { IconClose, IconDock } from '../ui/icons';
import { useIsMobile } from '../ui/useMediaQuery';

/**
 * Detachable cards. A card (tool options, the inspector, every side panel) sits in its docked
 * spot until its title bar is dragged; then it floats anywhere in the pane. Positions are kept
 * per device (localStorage), shared by all boards and both split panes. On phones cards are
 * sheets and never float.
 */

export type Pos = { x: number; y: number };

interface FloatingState {
  pos: Record<string, Pos>;
}

const KEY = 'vds:floating';

function load(): FloatingState {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<FloatingState>) : null;
    if (parsed && parsed.pos && typeof parsed.pos === 'object') return { pos: parsed.pos };
  } catch {
    /* ignore */
  }
  return { pos: {} };
}

export const floatingStore = createStore<FloatingState>(() => load());

floatingStore.subscribe((s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
});

export function setFloatingPos(key: string, pos: Pos | null): void {
  const next = { ...floatingStore.getState().pos };
  if (pos) next[key] = pos;
  else delete next[key];
  floatingStore.setState({ pos: next });
}

/** Keeps the card inside its pane; a card wider than the pane keeps its left edge visible. */
function clampPos(p: Pos, el: HTMLElement, host: HTMLElement): Pos {
  const pad = 8;
  return {
    x: Math.max(pad, Math.min(p.x, host.clientWidth - el.offsetWidth - pad)),
    y: Math.max(pad, Math.min(p.y, host.clientHeight - el.offsetHeight - pad)),
  };
}

export interface FloatingCard<T extends HTMLElement> {
  ref: RefObject<T | null>;
  floating: boolean;
  /** Inline position while floating. */
  style: CSSProperties | undefined;
  /** Spread onto the title bar: drag detaches and moves the card, double-click docks it. */
  handleProps: {
    onPointerDown?: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove?: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp?: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel?: (e: ReactPointerEvent<HTMLElement>) => void;
    onDoubleClick?: () => void;
  };
  dock: () => void;
}

/**
 * Floating behaviour for one card, identified by `key`. The card must be absolutely positioned
 * inside the pane (its offsetParent is the clamp area). `disabled` pins it in place.
 */
export function useFloatingCard<T extends HTMLElement>(key: string, disabled = false): FloatingCard<T> {
  const mobile = useIsMobile();
  const pos = useStore(floatingStore, (s) => s.pos[key] ?? null);
  const floating = !disabled && !mobile && pos !== null;
  const ref = useRef<T | null>(null);
  const drag = useRef<{ pointerId: number; dx: number; dy: number; moved: boolean } | null>(null);

  // A floating card stays inside the pane when the pane, or the card itself, changes size.
  useEffect(() => {
    if (!floating) return;
    const el = ref.current;
    const host = el?.offsetParent as HTMLElement | null;
    if (!el || !host) return;
    const fit = () => {
      const p = floatingStore.getState().pos[key];
      if (!p) return;
      const c = clampPos(p, el, host);
      if (c.x !== p.x || c.y !== p.y) setFloatingPos(key, c);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(host);
    ro.observe(el);
    return () => ro.disconnect();
  }, [floating, key]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0 || (e.target as Element).closest('button, input, select, a, label')) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    drag.current = { pointerId: e.pointerId, dx: e.clientX - r.left, dy: e.clientY - r.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const d = drag.current;
      const el = ref.current;
      const host = el?.offsetParent as HTMLElement | null;
      if (!d || d.pointerId !== e.pointerId || !el || !host) return;
      const hr = host.getBoundingClientRect();
      const next = { x: e.clientX - d.dx - hr.left, y: e.clientY - d.dy - hr.top };
      if (!d.moved) {
        // a click on the bar is not a drag: the card only detaches once it actually moves
        const r = el.getBoundingClientRect();
        if (Math.hypot(next.x - (r.left - hr.left), next.y - (r.top - hr.top)) < 3) return;
        d.moved = true;
      }
      setFloatingPos(key, clampPos(next, el, host));
    },
    [key],
  );

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  const dock = useCallback(() => setFloatingPos(key, null), [key]);

  const active = !disabled && !mobile;
  return {
    ref,
    floating,
    // a floated card may be dropped low: it ends 12px above the bottom of its host, like a docked one
    style: floating && pos ? { left: pos.x, top: pos.y, maxHeight: `calc(100% - ${Math.round(pos.y) + 12}px)` } : undefined,
    handleProps: active ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onDoubleClick: dock } : {},
    dock,
  };
}

/** The grip and (while floating) the dock button that every card's title bar carries. */
export function HandleControls({ card }: { card: Pick<FloatingCard<HTMLElement>, 'floating' | 'dock'> }) {
  return (
    <>
      <span className="opt-grip" aria-hidden />
      {card.floating && (
        <button type="button" className="opt-dock" title="Dock back in place" aria-label="Dock back in place" onClick={card.dock}>
          <IconDock />
        </button>
      )}
    </>
  );
}

const HANDLE_TITLE = 'Drag to move this panel anywhere (double-click docks it back)';

/** A side panel: docked at the top right, detachable by its title bar, with a close button. */
export function PanelFrame({
  kind,
  title,
  className,
  onClose,
  children,
}: {
  kind: string;
  title: string;
  className?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const card = useFloatingCard<HTMLElement>(`panel:${kind}`);
  return (
    <aside
      ref={card.ref}
      className={`panel${className ? ` ${className}` : ''}${card.floating ? ' is-floating' : ''}`}
      style={card.style}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="insp-head panel-handle" title={HANDLE_TITLE} {...card.handleProps}>
        <span>{title}</span>
        <HandleControls card={card} />
        <button type="button" className="panel-close" aria-label="Close" onClick={onClose}>
          <IconClose />
        </button>
      </div>
      {children}
    </aside>
  );
}

export { HANDLE_TITLE };
